import { ErrorHandler, Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import {
  STREAM_FILTER_VALUE,
  EventsFilterCurrentValues,
  EventsFilterMode,
  UserFilterCurrentValues,
  UserFilterFormValues,
  UserMarkerCustomData,
  UserMarkerWithCustomData
} from '../ui-components/gmap/gmap.interfaces';
import { DEFAULT_AVATAR_COORDINATE, MARKER_MODE } from '../avatar.constants';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from '@environments/environment';
import { MAP_VERSION } from '../ui-components/gmap/gmap.constants';
import { TranslateService } from '@ngx-translate/core';
import { Client, CoordinatesDataResponse, VideosIndexResponse } from '@api/client';
import { PostMapFocusRequest, PostMapTarget } from '../interfaces/post-map-target.interface';
import { PageLoadService } from '@app/services/page-load.service';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

const GOOGLE_MAPS_API_MAX_LOAD_ATTEMPTS = 2;
const GOOGLE_MAPS_API_RETRY_DELAY_MS = 1500;
const GOOGLE_MAPS_AUTH_FAILURE_REPORT_DELAY_MS = 0;
const GOOGLE_MAPS_AUTH_ERROR_NAME = 'GoogleMapsAuthenticationError';
const GOOGLE_MAPS_CONSOLE_ERROR_PREFIX = 'Google Maps JavaScript API error:';
const GOOGLE_MAPS_FATAL_ERROR_MARKERS: readonly string[] = [
  'referernotallowedmaperror',
  'billingnotenabledmaperror',
  'invalidkeymaperror',
  'missingkeymaperror',
  'apikeynotfounderror',
  'deletedapikeymaperror',
  'expiredkeymaperror',
  'projectdeniedmaperror',
  'apinotactivatedmaperror',
  'unauthorizedurlforclientidmaperror',
  'overquotamaperror',
];

@Injectable({
  providedIn: 'root'
})
export class GmapService {
  private readonly errorHandler = inject(ErrorHandler);
  private readonly translate = inject(TranslateService);
  private readonly client = inject(Client);
  private readonly pageLoad = inject(PageLoadService);

  public readonly markersLoadFailed = signal(false);

  //#region filters
  public centerLat: number = DEFAULT_AVATAR_COORDINATE.lat;
  public centerLon: number = DEFAULT_AVATAR_COORDINATE.lon;
  public centerReady$ = new BehaviorSubject<boolean>(false);
  public maxDistance: number = 10_000_000; // in meters
  public maxMarkerCount: number = 10_000;
  public userFilterInitialValues: UserFilterCurrentValues = {
    hasStreams: undefined,
    statuses: undefined,
    minRate: undefined,
    maxRate: undefined
  };
  public userFilterPreviousValues: UserFilterFormValues;
  public userFilterCurrentValuesSubject: BehaviorSubject<UserFilterCurrentValues> = new BehaviorSubject<UserFilterCurrentValues>(this.userFilterInitialValues);
  public eventsFilterInitialValues: EventsFilterCurrentValues = {
    mode: STREAM_FILTER_VALUE.TODAY,
    dateFrom: null,
    dateTo: null,
  };
  public eventsFilterCurrentValuesSubject: BehaviorSubject<EventsFilterCurrentValues> = new BehaviorSubject<EventsFilterCurrentValues>(this.eventsFilterInitialValues);
  //#endregion filters
  public imageCache: { [userId: number]: HTMLImageElement } = {};
  public myCoordinateId: string | undefined;
  public selectedMarkerCustomDataSubject: BehaviorSubject<UserMarkerCustomData | undefined> = new BehaviorSubject<UserMarkerCustomData>(undefined);
  public selectedMarkerCustomData$: Observable<UserMarkerCustomData | undefined> = this.selectedMarkerCustomDataSubject.asObservable();
  public inStreamSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public centeredInProgress: boolean = false;
  public isCentered: boolean = false;
  private userCenteredOverride: boolean = false;
  public selectedMarker: UserMarkerWithCustomData | null = null;
  public selectedUserId: number | null = null;
  public updateMarkersInProgressSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public updateMarkersInProgress$: Observable<boolean> = this.updateMarkersInProgressSubject.asObservable();
  private readonly googleMapsAuthFailureSubject: Subject<void> = new Subject<void>();
  public readonly googleMapsAuthFailure$: Observable<void> = this.googleMapsAuthFailureSubject.asObservable();
  private readonly refreshMarkersSubject: Subject<void> = new Subject<void>();
  public readonly refreshMarkers$: Observable<void> = this.refreshMarkersSubject.asObservable();
  private readonly postFocusRequestSubject: BehaviorSubject<PostMapFocusRequest | null> = new BehaviorSubject<PostMapFocusRequest | null>(null);
  public readonly postFocusRequest$: Observable<PostMapFocusRequest | null> = this.postFocusRequestSubject.asObservable();
  private markerModeSubject: BehaviorSubject<MARKER_MODE> = new BehaviorSubject<MARKER_MODE>(MARKER_MODE.STREAMS);
  public markerMode$: Observable<MARKER_MODE> = this.markerModeSubject.asObservable();
  public selectedUserMarker: BehaviorSubject<number> = new BehaviorSubject<number | undefined>(undefined);
  private apiWasLoaded: boolean = false;
  private apiLoadPromise: Promise<void> | null = null;
  private googleMapsAuthFailed: boolean = false;
  private googleMapsAuthFailureHandlerRegistered: boolean = false;
  private googleMapsAuthFailureReportTimeout: number | null = null;
  private googleMapsConsoleErrorCaptureRegistered: boolean = false;
  private googleMapsConsoleError: string | null = null;
  private lastGoogleMapsAuthFailureReport: string | null = null;

  public get updateMarkersInProgress(): boolean {
    return this.updateMarkersInProgressSubject.value;
  }

  public get markerMode(): MARKER_MODE {
    return this.markerModeSubject.value;
  }

  public set markerMode(value: MARKER_MODE) {
    this.markerModeSubject.next(value);
  }

  public requestMarkersRefresh(): void {
    this.refreshMarkersSubject.next();
  }

  public requestPostFocus(target: PostMapTarget): void {
    this.postFocusRequestSubject.next({
      requestId: this.buildPostFocusRequestId(target.postId),
      target,
    });
  }

  public clearPostFocusRequest(requestId: string): void {
    const current = this.postFocusRequestSubject.value;
    if (!current || current.requestId !== requestId) {
      return;
    }
    this.postFocusRequestSubject.next(null);
  }

  public get inStream(): boolean {
    return this.inStreamSubject.value;
  }

  public get hasUserAdjustedCenter(): boolean {
    return this.userCenteredOverride;
  }

  public markUserAdjustedCenter(): void {
    this.userCenteredOverride = true;
  }

  public resetUserAdjustedCenter(): void {
    this.userCenteredOverride = false;
  }

  public resetGoogleMapsAuthFailure(): void {
    this.googleMapsAuthFailed = false;
    this.apiWasLoaded = false;
    this.googleMapsConsoleError = null;
    this.clearGoogleMapsAuthFailureReportTimeout();
  }

  //#region load Google Maps Api
  private get isApiReady(): boolean {
    return typeof google !== 'undefined'
      && typeof google.maps !== 'undefined'
      && typeof google.maps.importLibrary === 'function';
  }

  public async loadGoogleMapsApiIfNeed(): Promise<void> {
    this.registerGoogleMapsConsoleErrorCapture();
    this.registerGoogleMapsAuthFailureHandler();

    if (!this.googleMapsAuthFailed && this.apiWasLoaded && this.isApiReady) {
      return;
    }
    if (this.apiLoadPromise) {
      return this.apiLoadPromise;
    }

    this.apiLoadPromise = this.loadGoogleMapsApi();
    try {
      await this.apiLoadPromise;
    } finally {
      this.apiLoadPromise = null;
    }
  }

  private registerGoogleMapsAuthFailureHandler(): void {
    if (this.googleMapsAuthFailureHandlerRegistered || typeof window === 'undefined') {
      return;
    }

    window.gm_authFailure = (): void => {
      this.googleMapsAuthFailed = true;
      this.apiWasLoaded = false;
      this.scheduleGoogleMapsAuthFailureReport();
      this.googleMapsAuthFailureSubject.next();
    };
    this.googleMapsAuthFailureHandlerRegistered = true;
  }

  private registerGoogleMapsConsoleErrorCapture(): void {
    if (this.googleMapsConsoleErrorCaptureRegistered || typeof console === 'undefined') {
      return;
    }

    const originalConsoleError = console.error.bind(console);
    console.error = (...data: unknown[]): void => {
      this.captureGoogleMapsConsoleError(data);
      originalConsoleError(...data);
    };
    this.googleMapsConsoleErrorCaptureRegistered = true;
  }

  private captureGoogleMapsConsoleError(data: readonly unknown[]): void {
    const isGoogleMapsApiError = data.some((value): value is string =>
      typeof value === 'string' && value.trimStart().startsWith(GOOGLE_MAPS_CONSOLE_ERROR_PREFIX)
    );

    if (!isGoogleMapsApiError) {
      return;
    }

    this.googleMapsConsoleError = data.map((value) => this.getErrorText(value)).join(' ');
    if (this.googleMapsAuthFailed) {
      this.scheduleGoogleMapsAuthFailureReport();
    }
  }

  private scheduleGoogleMapsAuthFailureReport(): void {
    if (this.googleMapsAuthFailureReportTimeout !== null) {
      return;
    }

    if (typeof window === 'undefined') {
      this.reportGoogleMapsAuthFailure();
      return;
    }

    this.googleMapsAuthFailureReportTimeout = window.setTimeout((): void => {
      this.googleMapsAuthFailureReportTimeout = null;
      this.reportGoogleMapsAuthFailure();
    }, GOOGLE_MAPS_AUTH_FAILURE_REPORT_DELAY_MS);
  }

  private reportGoogleMapsAuthFailure(): void {
    const error = this.createGoogleMapsAuthFailureError();
    if (this.lastGoogleMapsAuthFailureReport === error.message) {
      return;
    }

    this.lastGoogleMapsAuthFailureReport = error.message;
    this.errorHandler.handleError(error);
  }

  private clearGoogleMapsAuthFailureReportTimeout(): void {
    if (this.googleMapsAuthFailureReportTimeout === null || typeof window === 'undefined') {
      return;
    }

    window.clearTimeout(this.googleMapsAuthFailureReportTimeout);
    this.googleMapsAuthFailureReportTimeout = null;
  }

  private async loadGoogleMapsApi(): Promise<void> {
    if (this.googleMapsAuthFailed) {
      this.apiWasLoaded = false;
      throw this.createGoogleMapsAuthFailureError();
    }

    if (this.isApiReady) {
      this.apiWasLoaded = true;
      return;
    }

    for (let attempt = 1; attempt <= GOOGLE_MAPS_API_MAX_LOAD_ATTEMPTS; attempt += 1) {
      try {
        const loader = new Loader({
          apiKey: environment.googleMapApiKey,
          version: MAP_VERSION.QUARTERLY,
          retries: 0,
        });

        await loader.importLibrary('maps');
        if (this.googleMapsAuthFailed) {
          throw this.createGoogleMapsAuthFailureError();
        }
        this.apiWasLoaded = true;
        return;
      } catch (error) {
        const canRetry = this.shouldRetryGoogleMapsLoad(error, attempt);
        this.apiWasLoaded = false;

        console.warn('[GmapService] Google Maps API load failed', {
          attempt,
          maxAttempts: GOOGLE_MAPS_API_MAX_LOAD_ATTEMPTS,
          canRetry,
          isOnline: this.isNavigatorOnline(),
          message: this.getErrorText(error),
        });

        if (!canRetry) {
          console.error(this.translate.instant('avatar.gmapService.errorLoadMapApi'));
          console.error(error);
          throw error;
        }

        await this.waitForGoogleMapsRetry(attempt);
      }
    }
  }

  private shouldRetryGoogleMapsLoad(error: unknown, attempt: number): boolean {
    if (attempt >= GOOGLE_MAPS_API_MAX_LOAD_ATTEMPTS) {
      return false;
    }

    if (this.googleMapsAuthFailed) {
      return false;
    }

    if (this.isNavigatorOnline() === false) {
      return false;
    }

    return !this.isFatalGoogleMapsLoadError(error);
  }

  private createGoogleMapsAuthFailureError(): Error {
    const details = this.googleMapsConsoleError ?? 'Google Maps authentication failed';
    const pageOrigin = this.getPageOrigin();
    const error = new Error(`${details}\nPage origin: ${pageOrigin}`);
    error.name = GOOGLE_MAPS_AUTH_ERROR_NAME;
    return error;
  }

  private getPageOrigin(): string {
    if (typeof location === 'undefined') {
      return 'unavailable';
    }

    if (location.origin && location.origin !== 'null') {
      return location.origin;
    }

    return location.host ? `${location.protocol}//${location.host}` : location.protocol;
  }

  private isFatalGoogleMapsLoadError(error: unknown): boolean {
    const normalizedError = this.getErrorText(error).toLowerCase();

    return GOOGLE_MAPS_FATAL_ERROR_MARKERS.some((marker) => normalizedError.includes(marker));
  }

  private getErrorText(error: unknown): string {
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }

    if (typeof error === 'string') {
      return error;
    }

    return String(error);
  }

  private isNavigatorOnline(): boolean | null {
    if (typeof navigator === 'undefined') {
      return null;
    }

    return navigator.onLine;
  }

  private waitForGoogleMapsRetry(attempt: number): Promise<void> {
    const delayMs = GOOGLE_MAPS_API_RETRY_DELAY_MS * attempt;

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, delayMs);
    });
  }

  //#endregion load Google Maps Api

  public setUserFilterValues(hasStreams: boolean | undefined, statuses: number[] | undefined, minRate: number | undefined, maxRate: number | undefined): void {
    this.userFilterCurrentValuesSubject.next({hasStreams, statuses, minRate, maxRate});
  }

  public setEventsFilterMode(mode: EventsFilterMode): void {
    const current = this.eventsFilterCurrentValuesSubject.value;
    this.eventsFilterCurrentValuesSubject.next({
      ...current,
      mode,
    });
  }

  public setEventsFilterValues(values: EventsFilterCurrentValues): void {
    this.eventsFilterCurrentValuesSubject.next(values);
  }

  public setEventsFilterDateRange(dateFrom: string | null, dateTo: string | null): void {
    this.eventsFilterCurrentValuesSubject.next({
      mode: 'range',
      dateFrom,
      dateTo,
    });
  }

  public async getUserMarkerIndexWithFilters(): Promise<CoordinatesDataResponse | void> {
    try {
      return await firstValueFrom(this.pageLoad.read(this.client.coordinateIndex(
        this.centerLon,
        this.centerLat,
        this.maxDistance,
        this.maxMarkerCount,
        undefined,
        this.userFilterCurrentValuesSubject.value.minRate,
        this.userFilterCurrentValuesSubject.value.maxRate,
        this.userFilterCurrentValuesSubject.value.statuses,
        this.userFilterCurrentValuesSubject.value.hasStreams,
      )));
    } catch (error) {
      this.markersLoadFailed.set(true);
      console.error('Error fetching users markers data:', error);
    }
  }

  public async getStreamMarkerIndexWithFilters(
    group: boolean,
    count: number = this.maxMarkerCount,
    offset: number = undefined
  ): Promise<VideosIndexResponse | undefined> {
    const eventsFilter: EventsFilterCurrentValues = this.eventsFilterCurrentValuesSubject.value;
    const isLive: boolean | undefined = eventsFilter.mode === STREAM_FILTER_VALUE.LIVE ? true : undefined;
    const period: string | undefined = eventsFilter.mode === STREAM_FILTER_VALUE.TODAY ? 'day' : undefined;
    const dateFrom: string | undefined = eventsFilter.mode === 'range' && eventsFilter.dateFrom
      ? eventsFilter.dateFrom
      : undefined;
    const dateTo: string | undefined = eventsFilter.mode === 'range' && eventsFilter.dateTo
      ? eventsFilter.dateTo
      : undefined;

    try {
      return await firstValueFrom(this.pageLoad.read(this.client.videosIndex(
        this.centerLon,
        this.centerLat,
        this.maxDistance,
        count,
        offset,
        (this.markerMode === MARKER_MODE.STREAMS) ? isLive : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? period : undefined,
        (this.markerMode === MARKER_MODE.USERS),
        group,
        (this.markerMode === MARKER_MODE.STREAMS) ? dateFrom : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? dateTo : undefined,
      )));
    } catch (error) {
      this.markersLoadFailed.set(true);
      console.error('Error fetching streams markers data:', error);
    }
  }

  public async getUserStreamsWithFilters(
    userId: number,
    count: number,
    offset: number = undefined
  ): Promise<VideosIndexResponse | void> {
    const eventsFilter: EventsFilterCurrentValues = this.eventsFilterCurrentValuesSubject.value;
    const isLive: boolean | undefined = eventsFilter.mode === STREAM_FILTER_VALUE.LIVE ? true : undefined;
    const period: string | undefined = eventsFilter.mode === STREAM_FILTER_VALUE.TODAY ? 'day' : undefined;
    const dateFrom: string | undefined = eventsFilter.mode === 'range' && eventsFilter.dateFrom
      ? eventsFilter.dateFrom
      : undefined;
    const dateTo: string | undefined = eventsFilter.mode === 'range' && eventsFilter.dateTo
      ? eventsFilter.dateTo
      : undefined;

    try {
      return await firstValueFrom(this.pageLoad.read(this.client.videosUser(
        userId,
        (this.markerMode === MARKER_MODE.STREAMS) ? this.centerLon : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? this.centerLat : undefined,
        this.maxDistance,
        count,
        offset,
        (this.markerMode === MARKER_MODE.STREAMS) ? isLive : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? period : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? dateFrom : undefined,
        (this.markerMode === MARKER_MODE.STREAMS) ? dateTo : undefined,
      )));
    } catch (error) {
      console.error('Error fetching user streams data:', error);
    }
  }

  //#region Coordinates
  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  public getDistanceByCoordinates(point1: google.maps.LatLng, point2: google.maps.LatLng): number {
    const R: number = 6371; // Radius of the earth in km
    const lat1: number = point1.lat();
    const lon1: number = point1.lng();
    const lat2: number = point2.lat();
    const lon2: number = point2.lng();
    const dLat: number = this.deg2rad(lat2 - lat1);
    const dLon: number = this.deg2rad(lon2 - lon1);
    const a: number = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2))
      * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c: number = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d: number = R * c; // Distance in km
    return d * 1000;
  }
  //#endregion Coordinates

  public deleteImageFromCache(userId: number): void {
    if (!this.imageCache[userId]) return;

    delete this.imageCache[userId];
  }

  private buildPostFocusRequestId(postId: number): string {
    const random = Math.random().toString(36).slice(2, 10);
    return `post-${postId}-${Date.now()}-${random}`;
  }

  //#endregion load image
}
